import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import axios from 'axios';

const LS_KEY = 'pushToken';
let listenersRegistered = false;

async function registerTokenWithBackend(tokenValue) {
  if (!tokenValue) return;
  localStorage.setItem(LS_KEY, tokenValue);
  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
  if (platform === 'ios' || platform === 'android') {
    await axios.post('/api/push/register', { token: tokenValue, platform });
  }
}

async function registerAndroidFcmToken() {
  const fcmToken = await FCM.getToken();
  if (fcmToken?.token) {
    await registerTokenWithBackend(fcmToken.token);
  }
  return fcmToken?.token || null;
}

function ensureListeners() {
  if (listenersRegistered) return;
  listenersRegistered = true;

  // When the OS issues us a token, register it with the backend
  PushNotifications.addListener('registration', async (token) => {
    try {
      const platform = Capacitor.getPlatform();
      const apnsOrFcmToken = token?.value;

      // Prefer FCM token if available (required for iOS when sending via Firebase Admin)
      let finalToken = apnsOrFcmToken;
      try {
        const fcmToken = await FCM.getToken();
        if (fcmToken?.token) {
          finalToken = fcmToken.token;
        }
      } catch (e) {
        // If FCM plugin isn't available yet, fall back to the token we have
        // eslint-disable-next-line no-console
        console.warn('FCM getToken failed, falling back to APNs token', e?.message || e);
      }

      await registerTokenWithBackend(finalToken);
    } catch (e) {
      // Keep silent; app should function even if registration fails
      // eslint-disable-next-line no-console
      console.warn('Push token register failed', e?.message || e);
    }
  });

  PushNotifications.addListener('registrationError', (err) => {
    // eslint-disable-next-line no-console
    console.warn('Push registration error', err);
  });
}

export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  const platform = Capacitor.getPlatform();

  try {
    const permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive !== 'granted') {
      const reqStatus = await PushNotifications.requestPermissions();
      if (reqStatus.receive !== 'granted') return;
    }

    if (platform === 'android') {
      try {
        await registerAndroidFcmToken();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Android FCM token registration failed during init', e?.message || e);
      }
      return;
    }

    ensureListeners();
    await PushNotifications.register();

    try {
      await registerAndroidFcmToken();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('FCM getToken failed during init', e?.message || e);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('initPushNotifications failed', e?.message || e);
  }
}

export async function requestPushNotifications() {
  if (!Capacitor.isNativePlatform()) {
    return { ok: false, reason: 'not-native' };
  }
  const platform = Capacitor.getPlatform();

  try {
    const permStatus = await PushNotifications.checkPermissions();
    let status = permStatus.receive;
    if (status !== 'granted') {
      const reqStatus = await PushNotifications.requestPermissions();
      status = reqStatus.receive;
    }
    if (status !== 'granted') {
      return { ok: false, status };
    }

    if (platform === 'android') {
      try {
        await registerAndroidFcmToken();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Android FCM token registration failed during request', e?.message || e);
        return { ok: false, reason: 'android-fcm-token-failed' };
      }
      return { ok: true, status };
    }

    ensureListeners();
    await PushNotifications.register();

    try {
      await registerAndroidFcmToken();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('FCM getToken failed during request', e?.message || e);
    }
    return { ok: true, status };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('requestPushNotifications failed', e?.message || e);
    return { ok: false, reason: 'error' };
  }
}

export async function getPushPermissionStatus() {
  if (!Capacitor.isNativePlatform()) {
    return { status: 'not-native' };
  }
  try {
    const permStatus = await PushNotifications.checkPermissions();
    return { status: permStatus.receive || 'unknown' };
  } catch (e) {
    return { status: 'error' };
  }
}

export async function unregisterPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;
  const token = localStorage.getItem(LS_KEY);
  if (!token) return;

  try {
    await axios.post('/api/push/unregister', { token });
  } catch (e) {
    // ignore
  } finally {
    localStorage.removeItem(LS_KEY);
  }
}
